import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from './layouts/AppShell'
import { AuthLayout } from './layouts/AuthLayout'
import { PublicLayout } from './layouts/PublicLayout'
import { SettingsLayout } from './layouts/SettingsLayout'
import { RequireAuth } from './RequireAuth'
import { LoginPage } from '@/features/auth/LoginPage'
import { SsoCallbackPage } from '@/features/auth/SsoCallbackPage'
import { OnboardingPage } from '@/features/auth/OnboardingPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { MeetingsPage } from '@/features/meetings/MeetingsPage'
import { MeetingDetailPage } from '@/features/meetings/MeetingDetailPage'
import { SeriesDetailPage } from '@/features/meetings/SeriesDetailPage'
import { ActionsPage } from '@/features/actions/ActionsPage'
import { SearchPage } from '@/features/search/SearchPage'
import { AnalyticsPage } from '@/features/analytics/AnalyticsPage'
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage'
import { ProfileSettings } from '@/features/settings/panes/ProfileSettings'
import { WorkspaceSettings } from '@/features/settings/panes/WorkspaceSettings'
import { MembersSettings } from '@/features/settings/panes/MembersSettings'
import { RolesSettings } from '@/features/authorization'
import { PatronsSettings } from '@/features/patrons'
import { RecordingRulesSettings } from '@/features/settings/panes/RecordingRulesSettings'
import { LanguageSettings } from '@/features/settings/panes/LanguageSettings'
import { NotificationSettings } from '@/features/settings/panes/NotificationSettings'
import { BillingSettings } from '@/features/settings/panes/BillingSettings'
import { SecuritySettings } from '@/features/settings/panes/SecuritySettings'
import { TenantsPage } from '@/features/admin/TenantsPage'
import { AuditLogPage } from '@/features/admin/AuditLogPage'
import { VocabularyPage } from '@/features/admin/VocabularyPage'
import { SharedSummaryPage } from '@/features/share/SharedSummaryPage'
import { NotFoundPage } from '@/features/misc/NotFoundPage'
import { ErrorPage } from '@/features/misc/ErrorPage'

/**
 * The route table.
 *
 * Three trees, and the split is deliberate:
 *  - /login, /onboarding  -> AuthLayout, no app chrome
 *  - /share/:id           -> PublicLayout, reachable without a session
 *  - everything else      -> RequireAuth + AppShell
 *
 * Screens are imported eagerly. The whole bundle is small and every screen is
 * reachable in one or two clicks, so route-level code splitting would trade a
 * visible loading flash for a saving nobody would feel. Revisit when a screen
 * pulls in something heavy of its own (a PDF renderer, a waveform library) —
 * split that screen then, not the router now.
 */
export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/auth/callback', element: <SsoCallbackPage /> },
    ],
  },
  {
    element: <PublicLayout />,
    errorElement: <ErrorPage />,
    children: [{ path: '/share/:token', element: <SharedSummaryPage /> }],
  },
  {
    element: <RequireAuth />,
    errorElement: <ErrorPage />,
    children: [
      { path: '/onboarding', element: <OnboardingPage /> },
      {
        element: <AppShell />,
        children: [
          { index: true, path: '/', element: <DashboardPage /> },
          { path: '/meetings', element: <MeetingsPage /> },
          { path: '/meetings/:meetingId', element: <MeetingDetailPage /> },
          { path: '/meetings/series/:seriesId', element: <SeriesDetailPage /> },
          { path: '/actions', element: <ActionsPage /> },
          { path: '/search', element: <SearchPage /> },
          { path: '/analytics', element: <AnalyticsPage /> },
          { path: '/integrations', element: <IntegrationsPage /> },
          {
            path: '/settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="/settings/profile" replace /> },
              { path: 'profile', element: <ProfileSettings /> },
              { path: 'workspace', element: <WorkspaceSettings /> },
              { path: 'members', element: <MembersSettings /> },
              { path: 'roles', element: <RolesSettings /> },
              { path: 'patrons', element: <PatronsSettings /> },
              { path: 'recording', element: <RecordingRulesSettings /> },
              { path: 'languages', element: <LanguageSettings /> },
              { path: 'notifications', element: <NotificationSettings /> },
              { path: 'billing', element: <BillingSettings /> },
              { path: 'security', element: <SecuritySettings /> },
            ],
          },
          { path: '/admin/tenants', element: <TenantsPage /> },
          { path: '/admin/audit', element: <AuditLogPage /> },
          { path: '/admin/vocabulary', element: <VocabularyPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
])
